import React, { forwardRef, useMemo, useState, useEffect } from 'react';
import { Post, UserData, HydratedPost } from '../types';
import { FONT_OPTIONS } from '../constants';

const hexToRgba = (hex: string, opacity: number) => {
    try {
        let cleanHex = hex.replace('#', '');
        if (cleanHex.length === 3) {
            cleanHex = cleanHex[0] + cleanHex[0] + cleanHex[1] + cleanHex[1] + cleanHex[2] + cleanHex[2];
        }
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        return isNaN(r) || isNaN(g) || isNaN(b) ? null : `rgba(${r}, ${g}, ${b}, ${opacity})`;
    } catch (e) {
        return null;
    }
};

const StatBox = ({ value, label, icon, isEmojiInLabel = false }: { value: any, label: string, icon?: string, isEmojiInLabel?: boolean }) => (
    <div style={{
        width: '240px',
        height: '240px',
        backgroundColor: 'rgba(30,30,30,0.5)',
        borderRadius: '35px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2px',
    }}>
        <div style={{ fontSize: '56px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', color: 'white' }}>
            {value} {!isEmojiInLabel && icon && <span style={{ fontSize: '42px' }}>{icon}</span>}
        </div>
        <div style={{ fontSize: '15px', fontWeight: '900', opacity: 0.9, color: 'white', letterSpacing: '1px', textAlign: 'center', display: 'flex', alignItems: 'center', gap: '4px' }}>
            {label} {isEmojiInLabel && icon && <span style={{ fontSize: '18px' }}>{icon}</span>}
        </div>
    </div>
);


const getCorsUrl = (url?: string) => {
    if (!url) return undefined;
    if (url.startsWith('data:') || url.startsWith('blob:')) return url;
    // Append a unique-ish query param to bypass cache that might not have CORS headers
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}goodlyfe_screenshot=1`;
};

const getRgbaColor = (color: string, opacity: number) => {
    if (!color) return `rgba(26,26,46,${opacity})`;

    // If it's already rgba, replace the alpha channel
    if (color.startsWith('rgba')) {
        return color.replace(/[\d.]+\)$/, `${opacity})`);
    }
    // If it's rgb, convert to rgba
    if (color.startsWith('rgb')) {
        return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
    }

    // Handle hex
    let hex = color.replace('#', '');
    if (hex.length === 3 || hex.length === 4) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    // Fallback if parsing fails
    return `rgba(26,26,46,${opacity})`;
};

export const PostScreenshot = forwardRef<HTMLDivElement, { post: Post | HydratedPost, authorData?: UserData, postBlurOpacity?: number, mediaIndex?: number, inline?: boolean, bgOverrideUrl?: string, postsCount?: number, imageBgMode?: 'borderless' | 'border', communities?: any[] }>(({ post, authorData, postBlurOpacity = 80, mediaIndex, inline, bgOverrideUrl, postsCount = 0, imageBgMode = 'borderless', communities = [] }, ref) => {
    const hPost = post as HydratedPost;
    // If it's a bump, prioritize original post content for the screenshot if the bump itself is empty
    const isBump = post.type === 'bump';
    const displayPost = (isBump && hPost.originalPost && !post.content?.text && (!post.content?.media || post.content.media?.length === 0))
        ? hPost.originalPost
        : post;

    const isProfile = (displayPost.type as any) === 'profile';
    const isTextPost = displayPost.type === 'text' && (!displayPost.content?.media || displayPost.content.media.length === 0);
    const isAudio = displayPost.type === 'audio' || displayPost.type === 'voice';

    const mediaUrl = useMemo(() => {
        const content = displayPost.content;
        if (!content) return null;
        const mediaAtIdx = mediaIndex !== undefined ? content.media?.[mediaIndex] : null;
        const rawUrl = isAudio ? content.coverImageUrl : (mediaAtIdx?.url || content.media?.[0]?.url);
        return getCorsUrl(rawUrl);
    }, [isAudio, displayPost.content, mediaIndex]);

    const isVideoFile = useMemo(() => {
        if (!mediaUrl) return false;
        return displayPost.type === 'video' ||
            (displayPost.content?.media?.[0]?.type === 'video') ||
            mediaUrl.toLowerCase().includes('.mp4') ||
            mediaUrl.toLowerCase().includes('.webm') ||
            mediaUrl.toLowerCase().includes('.mov') ||
            mediaUrl.toLowerCase().includes('.avi') ||
            mediaUrl.toLowerCase().includes('.m4v') ||
            mediaUrl.toLowerCase().includes('/video/') ||
            mediaUrl.toLowerCase().includes('video') ||
            mediaUrl.startsWith('blob:');
    }, [mediaUrl, displayPost]);

    // In borderless mode, any post with media (image, video, audio) is rendered full-bleed
    const shouldFullBleedBg = imageBgMode === 'borderless' && !isProfile && !isTextPost &&
        (
            // has image
            Boolean(displayPost.content?.media?.some((m: any) => m.type === 'image' || (!m.type && m.url && !m.url.match(/\.(mp4|webm|mov|avi)$/i)))) ||
            // has video
            isVideoFile ||
            // has audio
            isAudio
        );
    const isBorderMode = imageBgMode === 'border';

    const profileAttachments = useMemo(() => {
        return authorData?.profileBgSlideshow?.attachments?.filter((a: any) => a.url) || [];
    }, [authorData?.profileBgSlideshow?.attachments]);

    const [activeSlideIndex, setActiveSlideIndex] = useState(0);

    useEffect(() => {
        if (!isProfile || profileAttachments.length <= 1) return;
        const intervalValue = authorData?.profileBgSlideshow?.intervalValue || 5;
        const intervalUnit = authorData?.profileBgSlideshow?.intervalUnit || 'seconds';
        let ms = intervalValue * 1000;
        if (intervalUnit === 'minutes') ms *= 60;
        if (intervalUnit === 'hours') ms *= 3600;

        const timer = setInterval(() => {
            setActiveSlideIndex(prev => (prev + 1) % profileAttachments.length);
        }, Math.max(2000, ms));

        return () => clearInterval(timer);
    }, [isProfile, profileAttachments, authorData?.profileBgSlideshow?.intervalValue, authorData?.profileBgSlideshow?.intervalUnit]);

    const isProfileBgColor = authorData?.profileBgMode === 'color';
    const activeSlideUrl = profileAttachments[activeSlideIndex]?.url || profileAttachments[0]?.url;
    const rawBgUrl = getCorsUrl(activeSlideUrl || authorData?.profileBgImage?.url || authorData?.profileBg?.url || authorData?.homeBgSlideshow?.attachments?.[0]?.url || authorData?.homeBgImage?.url || authorData?.homeBg?.url);
    // bgOverrideUrl lets the SharePostModal inject a chosen image as background.
    // For image/video/audio posts in full-bleed mode, use the post's own media as the background.
    let bgUrl = shouldFullBleedBg
        ? mediaUrl
        : bgOverrideUrl
            ? getCorsUrl(bgOverrideUrl)
            : ((!isProfileBgColor && (isProfile || isTextPost)) ? rawBgUrl : null);

    if (isBorderMode && bgUrl && bgUrl === mediaUrl) {
        bgUrl = null;
    }
    const hasBgImage = Boolean(bgUrl);

    const [bgAspectRatio, setBgAspectRatio] = useState<number | null>(null);

    useEffect(() => {
        if (bgUrl && (isProfile || shouldFullBleedBg)) {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                if (img.naturalWidth && img.naturalHeight) {
                    setBgAspectRatio(img.naturalWidth / img.naturalHeight);
                }
            };
            img.onerror = () => setBgAspectRatio(1); // fallback to square
            img.src = bgUrl;
            if (img.complete && img.naturalWidth && img.naturalHeight) {
                setBgAspectRatio(img.naturalWidth / img.naturalHeight);
            }
        } else if (!isProfile && !shouldFullBleedBg) {
            setBgAspectRatio(null);
        }
    }, [bgUrl, isProfile, shouldFullBleedBg]);

    // Use displayPost theme, fallback to author appThemeColor, then profileBg.style, then default
    let fallbackColor = authorData?.appThemeColor;
    if (!fallbackColor && authorData?.profileBg?.style) {
        // extract color from style string if needed, or use directly
        const match = authorData.profileBg.style.match(/background(?:-color)?:\s*([^;]+)/);
        if (match) fallbackColor = match[1].trim();
    }
    const themeColor = displayPost.themeColor || fallbackColor || '#3b82f6';
    const textColor = displayPost.textColor || authorData?.appTextColor || '#ffffff';
    const outlineColor = themeColor;

    // Fallbacks for missing author data (common with very old posts or deleted users)
    const displayName = authorData?.displayName || displayPost.author || 'GoodLyfe User';
    const username = displayPost.author || 'unknown';
    const pfp = getCorsUrl(authorData?.pfp) || 'https://i.pravatar.cc/150?u=goodlyfe_deleted';

    // Convert hex to rgb for translucent overlay bg
    const hexToRgb = (hex: string) => {
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        const fullHex = hex.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b);
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : { r: 59, g: 130, b: 246 };
    };

    const rgb = hexToRgb(themeColor);
    const alphaBg = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${postBlurOpacity / 100})`;
    const shouldBeOpaque = imageBgMode === 'borderless' || isTextPost;

    // The perfect thick sticker text shadow 
    const stickerTextShadow = `
        -1.5px -1.5px 0 ${outlineColor},  
        1.5px -1.5px 0 ${outlineColor},
        -1.5px  1.5px 0 ${outlineColor},
        1.5px  1.5px 0 ${outlineColor},
        -2px  0   0 ${outlineColor},
        2px  0   0 ${outlineColor},
        0   -2px 0 ${outlineColor},
        0    2px 0 ${outlineColor},
        0 4px 8px rgba(0,0,0,0.6)
    `;

    const width = '1080px';
    const height = (isProfile || shouldFullBleedBg)
        ? (bgAspectRatio ? `${Math.round(1080 / bgAspectRatio)}px` : (isProfile ? '607px' : '1080px'))
        : 'auto';

    // Calculate maximum characters and line count allowed within the 2:1 ratio (maxHeight: 2160px)
    const displayText = useMemo(() => {
        const text = displayPost.content?.text;
        if (!text) return '';

        // For full bleed bg posts, the media IS the background so text gets more room (no inline media block)
        const hasInlineMedia = Boolean(mediaUrl) && !shouldFullBleedBg;
        // Capped total height is 2160px.
        // Fixed overhead is ~350px (margins, padding, header, footer).
        // If media is present inline, it takes up to 1200px.
        const maxHeight = hasInlineMedia ? 610 : 1810;

        let fontSize = 60; // default 'med'
        switch (displayPost.textSize) {
            case 'xs': fontSize = 36; break;
            case 'sm': fontSize = 48; break;
            case 'med': fontSize = 60; break;
            case 'lg': fontSize = 90; break;
        }

        const lineRatio = 1.35;
        const maxLines = Math.max(1, Math.floor(maxHeight / (fontSize * lineRatio)));
        const charsPerLine = Math.max(10, Math.floor(976 / (fontSize * 0.45)));

        // Split text by lines and estimate how many visual lines they wrap to
        const paragraphs = text.split('\n');
        let totalRenderedLines = 0;
        const keptLines: string[] = [];
        let wasTruncated = false;

        for (const para of paragraphs) {
            const estimatedParaLines = Math.max(1, Math.ceil(para.length / charsPerLine));

            if (totalRenderedLines + estimatedParaLines > maxLines) {
                const remainingLines = maxLines - totalRenderedLines;
                if (remainingLines > 0) {
                    const charBudget = remainingLines * charsPerLine;
                    if (para.length > charBudget) {
                        keptLines.push(para.slice(0, Math.max(10, charBudget - 8)) + '... more');
                    } else {
                        keptLines.push(para);
                    }
                } else {
                    if (keptLines.length > 0) {
                        const lastIdx = keptLines.length - 1;
                        if (!keptLines[lastIdx].endsWith('... more')) {
                            keptLines[lastIdx] = keptLines[lastIdx].slice(0, Math.max(10, keptLines[lastIdx].length - 8)) + '... more';
                        }
                    }
                }
                wasTruncated = true;
                break;
            } else {
                keptLines.push(para);
                totalRenderedLines += estimatedParaLines;
            }
        }

        if (wasTruncated) {
            return keptLines.join('\n');
        }

        const totalCharLimit = maxLines * charsPerLine;
        if (text.length > totalCharLimit) {
            return text.slice(0, totalCharLimit - 8) + '... more';
        }

        return text;
    }, [displayPost.content?.text, mediaUrl, displayPost.textSize]);

    const activeFontFamily = useMemo(() => {
        const fontOption = FONT_OPTIONS.find(o => o.id === displayPost.fontId || (displayPost.fontId === 'default' && o.id === 'font_default'));
        return fontOption && fontOption.family !== 'inherit' ? fontOption.family : 'system-ui, -apple-system, sans-serif';
    }, [displayPost.fontId]);

    return (
        <div
            ref={ref}
            id="goodlyfe-screenshot-target"
            style={{
                position: inline ? 'relative' : 'fixed',
                left: inline ? undefined : '0',
                top: inline ? undefined : '0',
                width: width,
                height: height,
                minHeight: (isProfile || shouldFullBleedBg) ? height : '400px',
                maxHeight: (isProfile || shouldFullBleedBg) ? height : '2160px',
                backgroundColor: 'transparent',
                color: textColor,
                textShadow: stickerTextShadow,
                fontFamily: activeFontFamily,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                zIndex: inline ? 1 : -9999,
                pointerEvents: inline ? 'auto' : 'none',
                padding: 0,
                margin: 0,
                boxSizing: 'border-box'
            }}
        >
            <div style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: hasBgImage ? (isProfile ? 'black' : (shouldBeOpaque ? themeColor : 'transparent')) : alphaBg,
                zIndex: 0,
                borderRadius: isProfile ? '0px' : '48px',
                overflow: 'hidden'
            }}>
                {hasBgImage && (
                    isVideoFile ? (
                        <video
                            src={bgUrl!}
                            crossOrigin={inline ? undefined : "anonymous"}
                            style={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                zIndex: -1,
                                opacity: (isProfile || shouldFullBleedBg || shouldBeOpaque) ? 1 : 0.5
                            }}
                            autoPlay
                            muted
                            loop
                            playsInline
                        />
                    ) : (
                        <img
                            src={bgUrl!}
                            crossOrigin={inline ? undefined : "anonymous"}
                            style={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                zIndex: -1,
                                opacity: (isProfile || shouldFullBleedBg || shouldBeOpaque) ? 1 : 0.5
                            }}
                            alt="bg"
                        />
                    )
                )}
                {(isProfile || shouldFullBleedBg) && hasBgImage && (
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: isProfile
                            ? 'radial-gradient(circle at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,1) 100%)'
                            : 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.55) 100%)',
                        zIndex: 0
                    }} />
                )}
            </div>

            <div style={{
                position: 'relative',
                zIndex: 1,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                padding: isProfile ? '50px 60px' : '52px',
                boxSizing: 'border-box',
                justifyContent: 'center'
            }}>

                {isProfile ? (
                    /* PROFILE STORY CARD (ROW LAYOUT - CENTERED VERTICALLY INSIDE IMAGE ASPECT RATIO) */
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '48px', width: '100%', justifyContent: 'center' }}>
                        {/* Left Side: Large Avatar */}
                        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                                src={pfp}
                                style={{
                                    width: '300px',
                                    height: '300px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '4px solid rgba(255,255,255,0.4)',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
                                }}
                                alt="PFP"
                                crossOrigin="anonymous"
                                onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
                            />
                        </div>

                        {/* Right Side: Profile Details */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'left', gap: '8px' }}>
                            {/* Name & Handle */}
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '16px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '56px', fontWeight: 'bold', letterSpacing: '-0.02em', lineHeight: '1.1' }}>{displayName}</span>
                                <span style={{ fontSize: '34px', opacity: 0.8 }}>@{username}</span>
                            </div>

                            {/* Stats Counter */}
                            <div style={{
                                display: 'flex',
                                gap: '16px',
                                fontSize: '28px',
                                fontWeight: '600',
                                opacity: 0.95,
                                flexWrap: 'wrap',
                                marginTop: '4px',
                                color: '#ffffff'
                            }}>
                                <span>{postsCount} Posts</span>
                                <span style={{ opacity: 0.5 }}>•</span>
                                <span>{authorData?.followers?.length || 0} {authorData?.followerNickname || 'Friends'}</span>
                                <span style={{ opacity: 0.5 }}>•</span>
                                <span style={{ color: '#fbbf24' }}>{authorData?.totalPoints || 0} Lyfe pts</span>
                            </div>

                            {/* Bio */}
                            {authorData?.bio && (
                                <div style={{
                                    fontSize: '28px',
                                    fontStyle: 'italic',
                                    lineHeight: '1.4',
                                    opacity: 0.9,
                                    marginTop: '8px',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    maxHeight: '115px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical'
                                }}>
                                    {authorData.bio}
                                </div>
                            )}

                            {/* Footer / Branding */}
                            {!inline && (
                                <div style={{ marginTop: '12px', fontSize: '24px', opacity: 0.5, fontWeight: '600' }}>
                                    Be one of my {authorData?.followerNickname || 'friends'} on GoodLyfe!
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    /* POST CARD */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
                            <img
                                src={pfp}
                                style={{ width: '120px', height: '120px', borderRadius: '50%', objectFit: 'cover' }}
                                alt="PFP"
                                crossOrigin="anonymous"
                                onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
                            />
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontSize: '45px', fontWeight: 'bold' }}>{displayName}</span>
                                <span style={{ fontSize: '34px', opacity: 0.8 }}>@{username}</span>
                            </div>
                        </div>

                        {displayText && (
                            <div style={{
                                fontSize: (function () {
                                    switch (displayPost.textSize) {
                                        case 'xs': return '36px';
                                        case 'sm': return '48px';
                                        case 'med': return '60px';
                                        case 'lg': return '90px';
                                        default: return '60px';
                                    }
                                })(),
                                lineHeight: '1.35',
                                whiteSpace: 'pre-wrap',
                                fontWeight: '500'
                            }}>
                                {displayText}
                            </div>
                        )}

                        {/* Community Badge and/or Footprint Badge */}
                        {(() => {
                            const communityId = (post as any).communityId;
                            const footprintDetails = (post as any).footprintDetails;
                            const community = communityId ? communities.find((c: any) => c._id === communityId || c.id === communityId) : null;
                            if (!community && !footprintDetails) return null;
                            return (
                                <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', flexWrap: 'wrap', marginTop: '4px' }}>
                                    {community && (
                                        <div style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            backgroundColor: 'rgba(0,0,0,0.5)',
                                            borderRadius: '999px',
                                            padding: '10px 22px 10px 14px',
                                            border: '1px solid rgba(255,255,255,0.15)',
                                        }}>
                                            {community.avatar ? (
                                                <img src={getCorsUrl(community.avatar)} alt={community.name}
                                                    style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }}
                                                    crossOrigin="anonymous"
                                                    onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
                                                />
                                            ) : (
                                                <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: 'bold', color: 'white' }}>
                                                    {(community.name || '?')[0].toUpperCase()}
                                                </div>
                                            )}
                                            <span style={{ fontSize: '28px', fontWeight: '700', color: 'white' }}>{community.name}</span>
                                        </div>
                                    )}
                                    {footprintDetails && (
                                        <div style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            backgroundColor: 'rgba(0,0,0,0.5)',
                                            borderRadius: '999px',
                                            padding: '10px 22px 10px 14px',
                                            border: '1px solid rgba(255,255,255,0.15)',
                                        }}>
                                            <img src="/assets/footprint_logo.png"
                                                style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                                                alt="Footprint"
                                            />
                                            <span style={{ fontSize: '28px', fontWeight: '700', color: 'white' }}>{footprintDetails.achievementTitle || 'Footprint'}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Link Preview Card */}
                        {displayPost.type === 'link' && displayPost.content?.linkPreview && (
                            <div style={{
                                width: '100%',
                                backgroundColor: 'rgba(0,0,0,0.3)',
                                borderRadius: '28px',
                                border: '1px solid rgba(255,255,255,0.15)',
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                {displayPost.content.linkPreview.image && (
                                    <img
                                        src={getCorsUrl(displayPost.content.linkPreview.image)}
                                        style={{
                                            width: '100%',
                                            height: '400px',
                                            objectFit: 'cover',
                                            borderBottom: '1px solid rgba(255,255,255,0.15)'
                                        }}
                                        alt="Link Preview"
                                        crossOrigin="anonymous"
                                        onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                )}
                                <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <span style={{ fontSize: '24px', fontWeight: '900', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', letterSpacing: '2px' }}>
                                        {displayPost.content.linkPreview.domain || 'LINK'}
                                    </span>
                                    <span style={{ fontSize: '36px', fontWeight: 'bold', lineHeight: '1.2', color: 'white' }}>
                                        {displayPost.content.linkPreview.title || 'Shared Link'}
                                    </span>
                                    {displayPost.content.linkPreview.description && (
                                        <span style={{ fontSize: '28px', opacity: 0.8, lineHeight: '1.3', color: 'rgba(255,255,255,0.7)' }}>
                                            {displayPost.content.linkPreview.description}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Only show inline media for non-image posts (image posts use the media as the background) */}
                        {mediaUrl && !shouldFullBleedBg && (
                            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                {isVideoFile ? (
                                    <video
                                        src={mediaUrl}
                                        style={{
                                            maxWidth: '100%',
                                            height: 'auto',
                                            display: 'block',
                                            objectFit: 'contain',
                                            maxHeight: '1200px',
                                            borderRadius: '28px'
                                        }}
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        crossOrigin="anonymous"
                                        onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                ) : (
                                    <img
                                        src={mediaUrl}
                                        style={{
                                            maxWidth: '100%',
                                            height: 'auto',
                                            display: 'block',
                                            objectFit: 'contain',
                                            maxHeight: '1200px',
                                            borderRadius: '28px'
                                        }}
                                        alt="Post content"
                                        crossOrigin="anonymous"
                                        onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                )}
                            </div>
                        )}

                        {!inline && (
                            <div style={{ marginTop: '20px', fontSize: '32px', opacity: 0.7, fontWeight: 'bold' }}>Shared via GoodLyfe</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
});
